import React, { useState, useEffect } from 'react';
import { 
  UserPlusIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  ChatBubbleLeftIcon,
  CodeBracketIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ViewColumnsIcon,
  ListBulletIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { leadService } from '../services/leadService';
import { Lead, LeadStatus } from '../types/lead';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LeadCard from '../components/leads/LeadCard';
import LeadKanban, { LEAD_STAGES } from '../components/leads/LeadKanban';
import LeadFollowUpBar from '../components/leads/LeadFollowUpBar';
import LeadFormModal from '../components/leads/LeadFormModal';
import LeadConversionModal from '../components/leads/LeadConversionModal';
import LeadCommentsModal from '../components/leads/LeadCommentsModal';
import LeadEmbedModal from '../components/leads/LeadEmbedModal';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import ImportWizard from '../components/common/ImportWizard';
import { Switch } from '../components/common/Switch';
import { leadFieldMappings } from '../utils/fieldMappings';
import toast from 'react-hot-toast';

const Leads: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isConversionModalOpen, setIsConversionModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'board' | 'list'>(() => {
    try { return (localStorage.getItem('leads.viewMode') as 'board' | 'list') || 'board'; } catch { return 'board'; }
  });
  const switchView = (m: 'board' | 'list') => { setViewMode(m); try { localStorage.setItem('leads.viewMode', m); } catch {} };
  const [showConverted, setShowConverted] = useState(false);
  const [followUpFilter, setFollowUpFilter] = useState<'all' | 'overdue' | 'today' | 'tomorrow' | 'thisweek'>('all');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  useEffect(() => {
    if (user?.organizationId) {
      loadLeads();
    }
  }, [user?.organizationId]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter, showConverted, followUpFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds([]);
  }, [searchTerm, statusFilter, showConverted, itemsPerPage, followUpFilter]);

  const loadLeads = async () => {
    if (!user?.organizationId) return;
    
    try {
      setLoading(true);
      const [data, counts] = await Promise.all([
        leadService.getLeads(user.organizationId),
        leadService.getCommentCounts(user.organizationId),
      ]);
      setLeads(data);
      setCommentCounts(counts);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error(t('leads.messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (!showConverted && statusFilter !== 'converted') {
      filtered = filtered.filter(lead => lead.status !== 'converted');
    }

    // Státusz szűrés
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Emlékeztető szűrés
    if (followUpFilter !== 'all') {
      filtered = filtered.filter(lead => {
        if (!lead.nextFollowUpAt) return false;
        const followUpDate = lead.nextFollowUpAt instanceof Date
          ? lead.nextFollowUpAt
          : new Date(lead.nextFollowUpAt);
        const now = new Date();
        const diffMs = followUpDate.getTime() - now.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (followUpFilter === 'overdue') return diffMs < 0;
        if (followUpFilter === 'today') return diffMs >= 0 && diffHours <= 24;
        if (followUpFilter === 'tomorrow') return diffHours > 24 && diffHours <= 48;
        if (followUpFilter === 'thisweek') return diffHours > 48 && diffHours <= 168;

        return false;
      });
    }

    // Keresés
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.company?.toLowerCase().includes(term) ||
        lead.phone?.toLowerCase().includes(term)
      );
    }

    setFilteredLeads(filtered);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setIsFormModalOpen(true);
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsFormModalOpen(true);
  };

  const handleConvertLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsConversionModalOpen(true);
  };

  const handleDeleteLead = (leadId: string) => {
    setLeadToDelete(leadId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      await leadService.deleteLead(leadToDelete);
      toast.success(t('leads.messages.deleted'));
      loadLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error(t('leads.messages.errorDeleting'));
    } finally {
      setLeadToDelete(null);
    }
  };

  const handleFollowUpDone = async (lead: Lead, next: Date | null) => {
    try {
      await leadService.logContact(lead.id, next, lead.status, user?.id);
      toast.success(next ? `Átütemezve: ${next.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}` : 'Kapcsolatfelvétel rögzítve');
      loadLeads();
    } catch (error) {
      console.error('Error logging contact:', error);
      toast.error('Nem sikerült menteni');
    }
  };

  const handleStatusChange = async (leadId: string, status: LeadStatus) => {
    try {
      await leadService.updateLeadStatus(leadId, status, user?.id);
      toast.success(t('leads.messages.statusUpdated'));
      loadLeads();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('leads.messages.errorStatus'));
    }
  };

  const getPaginatedLeads = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLeads.slice(startIndex, endIndex);
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLeadIds(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    const paginated = getPaginatedLeads();
    if (selectedLeadIds.length === paginated.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(paginated.map(lead => lead.id));
    }
  };

  const handleBulkStatusChange = async (newStatus: LeadStatus) => {
    if (selectedLeadIds.length === 0) {
      toast.error('Nincs kiválasztott lead');
      return;
    }
    try {
      const promises = selectedLeadIds.map(leadId =>
        leadService.updateLeadStatus(leadId, newStatus, user?.id)
      );
      await Promise.all(promises);
      toast.success(`${selectedLeadIds.length} lead státusza frissítve`);
      setSelectedLeadIds([]);
      loadLeads();
    } catch (error) {
      console.error('Error bulk updating leads:', error);
      toast.error('Hiba a lead-ek frissítésekor');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Név', 'Email', 'Telefon', 'Cég', 'Státusz', 'Forrás', 'Létrehozva'];
    const rows = filteredLeads.map(lead => [
      lead.name,
      lead.email || '',
      lead.phone || '',
      lead.company || '',
      lead.status,
      lead.source,
      new Date(lead.createdAt).toLocaleDateString('hu-HU')
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Lead-ek CSV-be exportálva');
  };

  const handleViewComments = (lead: Lead) => {
    setSelectedLead(lead);
    setIsCommentsModalOpen(true);
  };

  const getStatusIcon = (status: LeadStatus) => {
    switch (status) {
      case 'new':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'contacted':
        return <UserGroupIcon className="h-5 w-5 text-yellow-500" />;
      case 'qualified':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'unqualified':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'converted':
        return <CheckCircleIcon className="h-5 w-5 text-purple-500" />;
      default:
        return null;
    }
  };

  const getStatusCount = (status: LeadStatus | 'all') => {
    if (status === 'all') return leads.length;
    return leads.filter(lead => lead.status === status).length;
  };

  const statusOptions: { value: LeadStatus | 'all'; label: string; color: string }[] = [
    { value: 'all', label: t('leads.status.all'), color: 'gray' },
    { value: 'new', label: t('leads.status.new'), color: 'blue' },
    { value: 'contacted', label: t('leads.status.contacted'), color: 'yellow' },
    { value: 'qualified', label: t('leads.status.qualified'), color: 'green' },
    { value: 'unqualified', label: t('leads.status.unqualified'), color: 'red' },
    { value: 'converted', label: t('leads.status.converted'), color: 'purple' },
  ];

  // Lapozó: csak releváns oldalszámok + ...
  const getVisiblePages = (current: number, total: number): (number | string)[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | string)[] = [1];
    if (current > 4) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 3) pages.push('...');
    pages.push(total);
    return pages;
  };

  // Pagination computed values
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = getPaginatedLeads();

  return (
    <div className="min-h-full">
      <CommonPageHeader
        title={t('leads.title')}
        subtitle={t('leads.subtitle')}
        icon={UserPlusIcon}
        onRefresh={loadLeads}
      >
        <div className="flex items-center gap-2">
          <button onClick={() => setIsEmbedModalOpen(true)} className="crm-btn crm-btn-secondary" title={t('leads.actions.embed')}>
            <CodeBracketIcon className="h-4 w-4" />
            <span className="hidden lg:inline">{t('leads.actions.embed')}</span>
          </button>
          <button onClick={() => setIsImportOpen(true)} className="crm-btn crm-btn-secondary" title="Import CSV / Excel">
            <ArrowUpTrayIcon className="h-4 w-4" />
            <span className="hidden lg:inline">Import</span>
          </button>
          <button onClick={handleExportCSV} className="crm-btn crm-btn-secondary" title="Export CSV">
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span className="hidden lg:inline">Export</span>
          </button>
          <button onClick={handleCreateLead} className="crm-btn crm-btn-primary">
            <PlusIcon className="h-4 w-4" />
            {t('leads.actions.newLead')}
          </button>
        </div>
      </CommonPageHeader>

      <div className="w-full space-y-4">
        {/* Mai követések */}
        {!loading && <LeadFollowUpBar leads={leads} onDone={handleFollowUpDone} onOpen={handleEditLead} />}

        {/* Filters */}
        <div className="crm-card p-3 sm:p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('leads.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as LeadStatus | 'all')}
                className="h-9 pl-3 pr-8 text-sm border rounded-lg max-w-[220px]"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({getStatusCount(option.value)})
                  </option>
                ))}
              </select>
            </div>

            {/* Follow-up Filter */}
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <select
                value={followUpFilter}
                onChange={(e) => setFollowUpFilter(e.target.value as any)}
                className="h-9 pl-3 pr-8 text-sm border rounded-lg max-w-[220px]"
              >
                <option value="all">Összes emlékeztető</option>
                <option value="overdue">Lejárt</option>
                <option value="today">Ma</option>
                <option value="tomorrow">Holnap</option>
                <option value="thisweek">Ezen a héten</option>
              </select>
            </div>

            {/* Konvertáltak mutatása – kapcsoló */}
            <label className="flex items-center gap-2 cursor-pointer select-none md:ml-auto">
              <Switch checked={showConverted} onChange={setShowConverted} size="sm" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Konvertáltak mutatása</span>
            </label>
          </div>
        </div>

        {/* Státusz-csempék + nézetváltó */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 flex-1">
            <button
              onClick={() => setStatusFilter('all')}
              className={`crm-card px-3 py-2 text-left transition-colors ${statusFilter === 'all' ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}
            >
              <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{t('leads.status.all')}</div>
              <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{getStatusCount('all')}</div>
            </button>
            {LEAD_STAGES.map(stage => (
              <button
                key={stage.id}
                onClick={() => setStatusFilter(stage.id)}
                className={`crm-card px-3 py-2 text-left transition-colors ${statusFilter === stage.id ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${stage.dot}`} />
                  <span className="truncate">{stage.label}</span>
                </div>
                <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{getStatusCount(stage.id)}</div>
              </button>
            ))}
          </div>
          <div className="hidden lg:flex items-center rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] p-0.5 flex-shrink-0">
            <button
              onClick={() => switchView('board')}
              className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-sm font-medium transition-colors ${viewMode === 'board' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
            >
              <ViewColumnsIcon className="w-4 h-4" />Tábla
            </button>
            <button
              onClick={() => switchView('list')}
              className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
            >
              <ListBulletIcon className="w-4 h-4" />Lista
            </button>
          </div>
        </div>

        {/* Leads Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <UserPlusIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nincs megjeleníthető lead
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Próbálj meg más szűrőket használni'
                : 'Kezdj el lead-eket hozzáadni vagy szinkronizálj Google Sheets-ből'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={handleCreateLead}
                className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Első Lead létrehozása
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Bulk actions toolbar */}
            {selectedLeadIds.length > 0 && (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {selectedLeadIds.length} lead kiválasztva
                  </span>
                  <button
                    onClick={() => setSelectedLeadIds([])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Kijelölés megszüntetése
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Státusz:</span>
                  {(['new', 'contacted', 'qualified', 'unqualified'] as LeadStatus[]).map(status => (
                    <button
                      key={status}
                      onClick={() => handleBulkStatusChange(status)}
                      className={`text-xs px-2 py-1 rounded-md font-medium ${
                        status === 'new' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                        status === 'contacted' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' :
                        status === 'qualified' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                        'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {status === 'new' ? 'Új' :
                       status === 'contacted' ? 'Kapcsolatba lépve' :
                       status === 'qualified' ? 'Minősített' :
                       'Nem minősített'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Pipeline-tábla – asztalon, ha ez a választott nézet */}
            {viewMode === 'board' && (
              <div className="hidden lg:block">
                <LeadKanban
                  leads={filteredLeads}
                  showConverted={showConverted || statusFilter === 'converted'}
                  onStatusChange={handleStatusChange}
                  onOpen={handleEditLead}
                  onConvert={handleConvertLead}
                  onViewComments={handleViewComments}
                  commentCounts={commentCounts}
                />
              </div>
            )}

            {/* Kártyák – mobilon mindig, asztalon lista nézetben 2xl alatt */}
            <div className={`${viewMode === 'board' ? 'lg:hidden' : '2xl:hidden'} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
              {paginatedLeads.map(lead => (
                <div key={lead.id} className="relative">
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </div>
                  <LeadCard
                    lead={lead}
                    onEdit={handleEditLead}
                    onDelete={handleDeleteLead}
                    onConvert={handleConvertLead}
                    onStatusChange={handleStatusChange}
                    onViewComments={handleViewComments}
                    commentCount={commentCounts[lead.id] || 0}
                  />
                </div>
              ))}
            </div>

            {/* Desktop view - Table (1536px+) – lista nézetben */}
            <div className={`${viewMode === 'board' ? 'hidden' : 'hidden 2xl:block'} crm-card overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={paginatedLeads.length > 0 && selectedLeadIds.length === paginatedLeads.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.contact')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.company')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.source')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.created')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('leads.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedLeads.map(lead => (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => handleEditLead(lead)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</div>
                        {lead.notes && <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{lead.notes}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {lead.email ? (
                          <a 
                            href={`mailto:${lead.email}`}
                            className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-all"
                          >
                            {lead.email}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {lead.phone ? (
                          <a 
                            href={`tel:${lead.phone}`}
                            className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-all"
                          >
                            {lead.phone}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{lead.company || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {/* Státusz helyben állítható – jelvénynek öltözött select */}
                        {(() => {
                          const stage = LEAD_STAGES.find(st => st.id === lead.status) || LEAD_STAGES[0];
                          return (
                            <span className="relative inline-flex items-center">
                              <span className={`w-1.5 h-1.5 rounded-full absolute left-2 pointer-events-none ${stage.dot}`} />
                              <select
                                value={lead.status}
                                onChange={(e) => handleStatusChange(lead.id, e.target.value as LeadStatus)}
                                title="Státusz módosítása"
                                className={`crm-input-raw appearance-none cursor-pointer h-7 pl-5 pr-6 rounded-full text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-primary-500 ${stage.soft}`}
                              >
                                {LEAD_STAGES.map(st => (
                                  <option key={st.id} value={st.id}>{st.label}</option>
                                ))}
                              </select>
                              <ChevronDownIcon className="w-3 h-3 absolute right-1.5 pointer-events-none opacity-60" />
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {t(`leads.source.${lead.source}`)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(lead.createdAt).toLocaleDateString('hu-HU')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewComments(lead); }}
                            className="relative p-1 rounded text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            title={t('leads.actions.viewComments')}
                          >
                            <ChatBubbleLeftIcon className="h-5 w-5" />
                            {(commentCounts[lead.id] || 0) > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary-600 text-white text-[10px] font-semibold flex items-center justify-center tabular-nums">
                                {commentCounts[lead.id]}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditLead(lead); }}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                            title={t('leads.actions.edit')}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          {lead.status !== 'converted' && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleConvertLead(lead); }}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                title={t('leads.actions.convert')}
                              >
                                <ArrowRightIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title={t('leads.actions.delete')}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('leads.pagination.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('leads.pagination.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('leads.pagination.showing')} <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, filteredLeads.length)}</span> {t('leads.pagination.of')} <span className="font-medium">{filteredLeads.length}</span> {t('leads.pagination.results')}
                  </p>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 dark:bg-gray-700 dark:text-white"
                  >
                    <option value={20}>20 / oldal</option>
                    <option value={50}>50 / oldal</option>
                    <option value={100}>100 / oldal</option>
                  </select>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>
                    {getVisiblePages(currentPage, totalPages).map((page, idx) => (
                      page === '...' ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-400 dark:text-gray-500"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page as number)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-600 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <LeadFormModal
          lead={selectedLead}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedLead(null);
          }}
          onSuccess={() => {
            setIsFormModalOpen(false);
            setSelectedLead(null);
            loadLeads();
          }}
        />
      )}

      {isConversionModalOpen && selectedLead && (
        <LeadConversionModal
          lead={selectedLead}
          onClose={() => {
            setIsConversionModalOpen(false);
            setSelectedLead(null);
          }}
          onSuccess={() => {
            setIsConversionModalOpen(false);
            setSelectedLead(null);
            loadLeads();
          }}
        />
      )}

      {selectedLead && (
        <LeadCommentsModal
          lead={selectedLead}
          isOpen={isCommentsModalOpen}
          onClose={() => {
            setIsCommentsModalOpen(false);
            setSelectedLead(null);
            if (user?.organizationId) leadService.getCommentCounts(user.organizationId).then(setCommentCounts);
          }}
        />
      )}

      <ImportWizard
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        mappings={leadFieldMappings}
        entityName="lead"
        templateFilename="leads_template"
        onImport={async (rows) => {
          const results = { success: 0, failed: 0, errors: [] as string[] };
          if (!user?.organizationId) {
            results.errors.push('Nincs szervezet kiválasztva');
            return results;
          }
          for (const row of rows) {
            try {
              // Firestore nem fogad undefined értéket – csak a kitöltött mezők mennek
              const data: Record<string, any> = {
                name: row.name,
                company: row.company,
                email: row.email,
                phone: row.phone,
                status: row.status || 'new',
                source: row.source || 'manual',
                score: row.score,
                nextFollowUpAt: row.nextFollowUpAt,
                notes: row.notes,
              };
              Object.keys(data).forEach(k => { if (data[k] === undefined || data[k] === '') delete data[k]; });
              await leadService.createLead(data as any, user.organizationId, user.id, user.displayName || user.email);
              results.success++;
            } catch (e: any) {
              results.failed++;
              results.errors.push(`${row.name || '(névtelen)'}: ${e?.message || 'hiba'}`);
            }
          }
          if (results.success > 0) loadLeads();
          return results;
        }}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setLeadToDelete(null);
        }}
        onConfirm={confirmDeleteLead}
        title={t('leads.actions.delete')}
        deleteMessage={t('leads.messages.deleteConfirm')}
        deleteWarning={t('leads.messages.deleteWarning')}
      />

      <LeadEmbedModal
        isOpen={isEmbedModalOpen}
        onClose={() => setIsEmbedModalOpen(false)}
      />
    </div>
  );
};

export default Leads;
